import React, { useState } from 'react'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import ViewReportButton from '../../../../components/ViewReportButton'
import Reports from './reports'
import { RetryNotice } from './RetryNotice'
import moment from 'moment'
import { hasAssessmentMarker } from '../../../../utils/assessmentSession'
import {
  AssessmentInfoCard,
  AssessmentdisplayInfo,
  AssessmentTitle,
  AssessmentSubtitle,
  TimeInfo,
  DaysLeft,
  DeadlineInfo,
  DeadlineLabel,
  DeadlineTime,
  TakeAssessmentButton,
  CompletedAssessmentCard,
  CompletedInfo,
  CompletedDate,
  CompletedLabel,
  CompletedDateTime,
  EntityTag,
  InstituteTag,
  EmptyStateMessage,
  RegularAssessmentsSection,
  RegularAssessmentsSectionTitle,
  DisabledTooltip,
  DisabledTooltipTitle,
  DisabledTooltipMessage,
  CopyLinkButton,
  SpecBadge,
  SpecBadgeContainer,
  SectionLoadMoreButton,
  FilteredListWrapper,
} from '../../Style'

const COLLAPSE_LIMIT = 3

const CollapsibleSection = ({ assessments, renderCard }) => {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? assessments : assessments.slice(0, COLLAPSE_LIMIT)
  const hasMore = assessments.length > COLLAPSE_LIMIT && !expanded

  return (
    <>
      {visible.map(renderCard)}
      {hasMore && (
        <SectionLoadMoreButton onClick={() => setExpanded(true)}>
          Show {assessments.length - COLLAPSE_LIMIT} more
        </SectionLoadMoreButton>
      )}
    </>
  )
}

const AssessmentTable = ({
  activeTab,
  activeAssessments = [],
  completedAssessments = [],
  handleTakeAssessment,
  handleViewReport,
  handleCopyAssessmentLink,
  activeAssessmentsLoading,
  completedAssessmentsLoading,
  dropoutAssessments = [],
  dropoutAssessmentsLoading,
  showTitle = false,
  diagnosisCompleted = false,
  individualDiagnosisStatus = { communication: true, aptitude: true },
  hasDiagnosisSections = false,
  activeEntityTab = 'institute',
}) => {
  const capitalizeFirstLetter = (string) => {
    if (!string) return ''
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const calculateDaysLeft = endTime => {
    const now = moment()
    // endTime carries IST wall-clock digits in a UTC-shaped value. Convert it
    // to the real instant for arithmetic (23:59 stored => 18:29Z).
    const end = moment.utc(endTime).subtract(5, 'hours').subtract(30, 'minutes')
    const days = end.diff(now, 'days')
    return `${days} days left`
  }

  const formatDateTime = dateTime => {
    // Render exactly what the backend sends (UTC), without shifting into the
    // browser's local timezone — otherwise IST adds +5:30 to the end time.
    return `${moment.utc(dateTime).format('MMM DD, YYYY hh:mm A')} IST`
  }

  // Function to check if an assessment should be disabled based on its type
  const isAssessmentDisabled = (assessment) => {
    // For Corporate tab, all assessments are enabled
    if (activeEntityTab !== 'institute') {
      return false
    }

    // One-time assessments are always enabled (not blocked by diagnosis)
    if (assessment.is_one_time === true) {
      return false
    }

    // Progression gate: the previous same-type assessment's result is still
    // being processed (backend sets this only for scheduled Comm/Aptitude).
    if (assessment.progressionLocked === true) {
      return true
    }

    // Diagnosis gate, decided by the server from the student's full record.
    // The list-derived check below only sees what the deadline filter and
    // search left on screen, so on its own it unlocked a scheduled run whenever
    // they hid the diagnosis rows.
    if (assessment.diagnosisLocked === true) {
      return true
    }

    // For Institute tab, check individual diagnosis status
    if (assessment.type === 'Communication') {
      return !individualDiagnosisStatus.communication
    } else if (assessment.type === 'Aptitude') {
      return !individualDiagnosisStatus.aptitude
    }

    // For other assessment types, they are enabled by default
    return false
  }

  // Function to get tooltip message for disabled assessments
  const getDisabledTooltipMessage = (assessment) => {
    if (activeEntityTab !== 'institute') {
      return null
    }

    // No tooltip for one-time assessments (they're never disabled)
    if (assessment.is_one_time === true) {
      return null
    }

    // Progression gate takes precedence over the diagnosis tooltip.
    if (assessment.progressionLocked === true) {
      return {
        title: 'Processing previous result',
        message: 'Complete any available retry first, or wait for your previous assessment result.'
      }
    }

    if (
      assessment.type === 'Communication' &&
      (assessment.diagnosisLocked === true || !individualDiagnosisStatus.communication)
    ) {
      return {
        title: 'Complete Communication Diagnosis',
        message: 'Initial Diagnosis assessments pending'
      }
    } else if (
      assessment.type === 'Aptitude' &&
      (assessment.diagnosisLocked === true || !individualDiagnosisStatus.aptitude)
    ) {
      return {
        title: 'Complete Aptitude Diagnosis',
        message: 'Initial Diagnosis assessments pending'
      }
    }

    return null
  }

  // Loading states
  if (
    (activeTab === 'active' && activeAssessmentsLoading) ||
    (activeTab === 'completed' && completedAssessmentsLoading) ||
    (activeTab === 'dropout' && dropoutAssessmentsLoading)
  ) {
    return <LoadingSpinner />
  }

  if (activeTab === 'reports') {
    return <Reports />
  }

const renderActiveAssessments = () => {
  if (activeAssessmentsLoading) {
    return <div>Loading active assessments...</div>
  }

  // Diagnosis assessments have their own section above, so they are filtered
  // out here. Keyed on the backend's is_diagnosis flag (the stored fact
  // admin-node writes at assignment time), NOT on the map being named
  // "Assessment #1" / "Assessment #2": renaming a diagnosis float used to make
  // it show up twice, and — worse — a diagnosis the section above had dropped
  // was hidden here too, so it could not be taken at all.
  // The condition MIRRORS the diagnosis section's own filter, so a row can
  // never be dropped by both and disappear entirely.
  const regularAssessments = activeAssessments.filter(
    assessment =>
      !(assessment.is_diagnosis === true && assessment.is_one_time !== true)
  )

  // Separate direct assignments from broadcasts
  const directAssessments = regularAssessments.filter(a => a.rowSource !== 'broadcast')
  const broadcastForSpec = regularAssessments.filter(a => a.rowSource === 'broadcast' && a.broadcastMatchesSpec)
  const broadcastOther = regularAssessments.filter(a => a.rowSource === 'broadcast' && !a.broadcastMatchesSpec)

  if (!regularAssessments || regularAssessments.length === 0) {
    return (
      <EmptyStateMessage>
        {hasDiagnosisSections
          ? 'No additional assessments available. Complete the diagnosis assessments above to unlock more.'
          : 'No active assessments available.'
        }
      </EmptyStateMessage>
    )
  }

  const renderAssessmentCard = (assessment) => {
    // Single-active-attempt: a started (INPROGRESS) assessment shows here but
    // cannot be started again — it renders as a disabled "In Progress" button.
    // The ONLY exception is a diagnosis assessment on the browser that started
    // it (holds the resume marker): diagnosis is resumable after a refresh /
    // tab-leave, every other assessment is single-shot.
    const canResume =
      assessment.is_diagnosis === true &&
      hasAssessmentMarker(assessment.assessment_assigned_id)
    const isInProgressElsewhere =
      assessment.status === 'INPROGRESS' && !canResume
    const disabled = isAssessmentDisabled(assessment) || isInProgressElsewhere
    const tooltipMessage = getDisabledTooltipMessage(assessment)
    const isBroadcast = assessment.rowSource === 'broadcast'

    return (
      <AssessmentInfoCard key={assessment.id || assessment.broadcastId} style={isBroadcast ? { borderLeft: '3px solid #3249D7' } : {}}>
        <AssessmentdisplayInfo>
          <AssessmentTitle>
            {capitalizeFirstLetter(assessment.title || assessment.name)}
            {assessment.isCorporate && assessment.entityName && (
              <EntityTag>Corporate</EntityTag>
            )}
            {!assessment.isCorporate && assessment.entityName && (
              <InstituteTag>Institute</InstituteTag>
            )}
          </AssessmentTitle>
          <AssessmentSubtitle>
            {assessment.roleName || assessment.type}
          </AssessmentSubtitle>
          <RetryNotice assessment={assessment} />
          {isBroadcast && assessment.broadcastScope?.length > 0 && (
            <SpecBadgeContainer>
              {assessment.broadcastScope.map((scope, idx) => (
                <SpecBadge key={`scope-${idx}`}>{scope}</SpecBadge>
              ))}
              {assessment.labelSpecialisations?.map((spec, idx) => (
                <SpecBadge key={`spec-${idx}`} style={{ background: '#F0FFF4', color: '#059669', borderColor: '#D1FAE5' }}>{spec}</SpecBadge>
              ))}
            </SpecBadgeContainer>
          )}
          {!isBroadcast && assessment.labelSpecialisations?.length > 0 && (
            <SpecBadgeContainer>
              {assessment.labelSpecialisations.map((spec, idx) => (
                <SpecBadge key={idx}>{spec}</SpecBadge>
              ))}
            </SpecBadgeContainer>
          )}
          {assessment.isCorporate && (
            <AssessmentSubtitle>
              By Corporate : {assessment.entityName}{' '}
            </AssessmentSubtitle>
          )}
          {!assessment.isCorporate && (
            <AssessmentSubtitle>
              By Institute : {assessment.entityName}{' '}
            </AssessmentSubtitle>
          )}
        </AssessmentdisplayInfo>
        <TimeInfo>
          {!isBroadcast && (
            <>
              <DaysLeft>{calculateDaysLeft(assessment.endTime)}</DaysLeft>
              <DeadlineInfo>
                <DeadlineLabel>End Time:</DeadlineLabel>
                <DeadlineTime>
                  {formatDateTime(assessment.endTime)}
                </DeadlineTime>
              </DeadlineInfo>
            </>
          )}
          <TakeAssessmentButton
            disabled={disabled}
            onClick={() => !disabled && handleTakeAssessment(assessment)}
            style={{
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
          >
            {isInProgressElsewhere ? 'In Progress' : assessment.isIntegrityRetry ? 'Retry Assessment' : 'Take Assessment'}
            {disabled && !isInProgressElsewhere && tooltipMessage && (
              <DisabledTooltip className="disabled-tooltip">
                <DisabledTooltipMessage>{tooltipMessage.message}</DisabledTooltipMessage>
              </DisabledTooltip>
            )}
          </TakeAssessmentButton>
        </TimeInfo>
      </AssessmentInfoCard>
    )
  }

  const listKey = [directAssessments.length, broadcastForSpec.length, broadcastOther.length].join('-')

  return (
    <FilteredListWrapper key={listKey}>
      {directAssessments.map(renderAssessmentCard)}

      {broadcastForSpec.length > 0 && (
        <CollapsibleSection
          assessments={broadcastForSpec}
          renderCard={renderAssessmentCard}
        />
      )}

      {broadcastOther.map(renderAssessmentCard)}
    </FilteredListWrapper>
  )
}

const renderCompletedAssessments = () => {
    if (completedAssessmentsLoading) {
      return <div>Loading completed assessments...</div>
    }

    if (!completedAssessments || completedAssessments.length === 0) {
      return (
        <EmptyStateMessage>
          No completed assessments available
        </EmptyStateMessage>
      )
    }

    // Three-tier split (same as Active tab)
    const directDone = completedAssessments.filter(a => a.rowSource !== 'broadcast')
    const broadcastSpecDone = completedAssessments.filter(a => a.rowSource === 'broadcast' && a.broadcastMatchesSpec)
    const broadcastOtherDone = completedAssessments.filter(a => a.rowSource === 'broadcast' && !a.broadcastMatchesSpec)

    const renderCompletedCard = (assessment) => {
      const isBroadcast = assessment.rowSource === 'broadcast'
      return (
        <CompletedAssessmentCard key={assessment.id || assessment.broadcastId} style={isBroadcast ? { borderLeft: '3px solid #3249D7' } : {}}>
          <CompletedInfo>
            <AssessmentdisplayInfo>
              <AssessmentTitle>
                {capitalizeFirstLetter(assessment.title || assessment.name)}
                {assessment.isCorporate && assessment.entityName && (
                  <EntityTag>Corporate</EntityTag>
                )}
                {!assessment.isCorporate && assessment.entityName && (
                  <InstituteTag>Institute</InstituteTag>
                )}
              </AssessmentTitle>
              <AssessmentSubtitle>
                {assessment.roleName || assessment.type}
              </AssessmentSubtitle>
              {isBroadcast && assessment.broadcastScope?.length > 0 && (
                <SpecBadgeContainer>
                  {assessment.broadcastScope.map((scope, idx) => (
                    <SpecBadge key={`scope-${idx}`}>{scope}</SpecBadge>
                  ))}
                  {assessment.labelSpecialisations?.map((spec, idx) => (
                    <SpecBadge key={`spec-${idx}`} style={{ background: '#F0FFF4', color: '#059669', borderColor: '#D1FAE5' }}>{spec}</SpecBadge>
                  ))}
                </SpecBadgeContainer>
              )}
              {!isBroadcast && assessment.labelSpecialisations?.length > 0 && (
                <SpecBadgeContainer>
                  {assessment.labelSpecialisations.map((spec, idx) => (
                    <SpecBadge key={idx}>{spec}</SpecBadge>
                  ))}
                </SpecBadgeContainer>
              )}
              {assessment.isCorporate && (
                <AssessmentSubtitle>
                  By Corporate : {assessment.entityName}{' '}
                </AssessmentSubtitle>
              )}
              {!assessment.isCorporate && (
                <AssessmentSubtitle>
                  By Institute : {assessment.entityName}{' '}
                </AssessmentSubtitle>
              )}
            </AssessmentdisplayInfo>
            <CompletedDate>
              <CompletedLabel>Completed on</CompletedLabel>
              <CompletedDateTime>
                {moment(assessment.submittedAt).format('DD/MM/YYYY')}
                <br />
                {moment(assessment.submittedAt).format('hh:mm A')}
              </CompletedDateTime>
            </CompletedDate>

            {/* Show ViewReportButton only for non-corporate assessments.
                Institute-assigned AI Interview rows DO get the button —
                only the corporate flavour is hidden. The !isCorporate
                check above already covers that for AI Interview too. */}
            {!assessment.isCorporate && assessment.type !== 'Custom_Assessment' && (
              <ViewReportButton
                onClick={() => handleViewReport(assessment.assessment_assigned_id)}
                disabled={assessment.scoresCalculated === false}
                endTime={assessment.endTime}
                aria-label="View Report"
              />
            )}
          </CompletedInfo>
        </CompletedAssessmentCard>
      )
    }

    const completedListKey = [directDone.length, broadcastSpecDone.length, broadcastOtherDone.length].join('-')

    return (
      <FilteredListWrapper key={completedListKey}>
        {directDone.map(renderCompletedCard)}

        {broadcastSpecDone.length > 0 && (
          <CollapsibleSection
              assessments={broadcastSpecDone}
            renderCard={renderCompletedCard}
          />
        )}

        {broadcastOtherDone.map(renderCompletedCard)}
      </FilteredListWrapper>
    )
  }

  const renderDropoutAssessments = () => {
    if (dropoutAssessmentsLoading) {
      return <div>Loading dropout assessments...</div>
    }

    if (!dropoutAssessments || dropoutAssessments.length === 0) {
      return (
        <EmptyStateMessage>
          No dropout assessments available
        </EmptyStateMessage>
      )
    }

    // Three-tier split (same as Active tab)
    const directDrop = dropoutAssessments.filter(a => a.rowSource !== 'broadcast')
    const broadcastSpecDrop = dropoutAssessments.filter(a => a.rowSource === 'broadcast' && a.broadcastMatchesSpec)
    const broadcastOtherDrop = dropoutAssessments.filter(a => a.rowSource === 'broadcast' && !a.broadcastMatchesSpec)

    const renderDropoutCard = (assessment) => {
      const isBroadcast = assessment.rowSource === 'broadcast'
      return (
        <CompletedAssessmentCard key={assessment.id || assessment.broadcastId} style={isBroadcast ? { borderLeft: '3px solid #3249D7' } : {}}>
          <CompletedInfo>
            <AssessmentdisplayInfo>
              <AssessmentTitle>
                {capitalizeFirstLetter(assessment.title || assessment.name)}
                {assessment.isCorporate && assessment.entityName && (
                  <EntityTag>Corporate</EntityTag>
                )}
                {!assessment.isCorporate && assessment.entityName && (
                  <InstituteTag>Institute</InstituteTag>
                )}
              </AssessmentTitle>
              <AssessmentSubtitle>
                {assessment.roleName || assessment.type}
              </AssessmentSubtitle>
              {isBroadcast && assessment.broadcastScope?.length > 0 && (
                <SpecBadgeContainer>
                  {assessment.broadcastScope.map((scope, idx) => (
                    <SpecBadge key={`scope-${idx}`}>{scope}</SpecBadge>
                  ))}
                  {assessment.labelSpecialisations?.map((spec, idx) => (
                    <SpecBadge key={`spec-${idx}`} style={{ background: '#F0FFF4', color: '#059669', borderColor: '#D1FAE5' }}>{spec}</SpecBadge>
                  ))}
                </SpecBadgeContainer>
              )}
              {!isBroadcast && assessment.labelSpecialisations?.length > 0 && (
                <SpecBadgeContainer>
                  {assessment.labelSpecialisations.map((spec, idx) => (
                    <SpecBadge key={idx}>{spec}</SpecBadge>
                  ))}
                </SpecBadgeContainer>
              )}
              <AssessmentSubtitle>Status: Dropout</AssessmentSubtitle>
              {assessment.isCorporate && (
                <AssessmentSubtitle>
                  By Corporate : {assessment.entityName}{' '}
                </AssessmentSubtitle>
              )}
              {!assessment.isCorporate && (
                <AssessmentSubtitle>
                  By Institute : {assessment.entityName}{' '}
                </AssessmentSubtitle>
              )}
            </AssessmentdisplayInfo>
            <CompletedDate>
              <CompletedLabel>Attempted on</CompletedLabel>
              <CompletedDateTime>
                {assessment.attemptedAt ? (
                  <>
                    {moment(assessment.attemptedAt).format('DD/MM/YYYY')}
                    <br />
                  </>
                ) : (
                  <>
                    {moment(assessment.createdAt || new Date()).format('DD/MM/YYYY')}
                    <br />
                    {moment(assessment.createdAt || new Date()).format('hh:mm A')}
                  </>
                )}
              </CompletedDateTime>
            </CompletedDate>
            {/* No View Report button for dropout assessments */}
          </CompletedInfo>
        </CompletedAssessmentCard>
      )
    }

    const dropListKey = [directDrop.length, broadcastSpecDrop.length, broadcastOtherDrop.length].join('-')

    return (
      <FilteredListWrapper key={dropListKey}>
        {directDrop.map(renderDropoutCard)}

        {broadcastSpecDrop.length > 0 && (
          <CollapsibleSection
              assessments={broadcastSpecDrop}
            renderCard={renderDropoutCard}
          />
        )}

        {broadcastOtherDrop.map(renderDropoutCard)}
      </FilteredListWrapper>
    )
  }

  return (
    <div>
      {activeTab === 'active' && renderActiveAssessments()}
      {activeTab === 'completed' && renderCompletedAssessments()}
      {activeTab === 'dropout' && renderDropoutAssessments()}
    </div>
  )
}

export default AssessmentTable
